import { ItemEditContent } from "@eusoft/webapp-framework";
import { forModel } from "@eusoft/webapp-jsx";
import { SingleSelectorTemplates, enumItemsSource, formatText, notEmpty, required, staticItemsSource, validEmail } from "@eusoft/webapp-ui";
import { AddressEditor } from "../../components/AddressEditor";
import { SalePointType } from "../../entities/Commands";
import { entityItemsSource, merchantItemsSource } from "../../helpers/ItemsSources";
import { Authorize } from "../../services/Authorize";
import { executeAsync, invalidateCache } from "../../services/DataService";
import { MerchantServiceType, type Guid, type ICreateSalePoint, type ISalePointListView, type IUpdateSalePoint} from "../../entities/Commands";
import type { IItemEditArgs} from "@eusoft/webapp-framework";

export interface IEditSalePointArgs extends IItemEditArgs<IUpdate> {
    id: Guid;
    listItem?: ISalePointListView;
}

export interface ICreateSalePointArgs extends IItemEditArgs<ICreate> {
    saleCircuitId?: Guid;
}

interface ICreate extends ICreateSalePoint {
    id: Guid;
    createMerchant: boolean;
}

interface IUpdate extends IUpdateSalePoint {
    hasAddress: boolean;
    merchantId: Guid;
}



export const CreateSalePointPage = ItemEditContent.bulder<ICreate, ICreateSalePointArgs>()

    .info({  
        name: "sale-point-create",
        route: "/sale-point/create",
    })

    .options(() => ({
        title: formatText("create-item", "sale-point"),
        style: ["panel"],
    }))

    .feature(Authorize<ICreateSalePointArgs>(bld =>
        bld.businessAccount()
            .resource("SalePoint", "create")))

    .save(async value => {

        value.type = parseInt(value.type as any);

        const id = await executeAsync("CreateSalePoint", value);

        value.id = id;

        invalidateCache("ListSalePoint");

        return true;
    })

    .load(async args => {

        args.value = {
            saleCircuitId: args.saleCircuitId,
            createMerchant: false
        } as ICreate;

        return true;
    })

    .editor(bld => <>

        {bld.singleSelector(a => a.saleCircuitId, {
            validators: [required],
            label: "sale-circuit",
            editor: {
                itemsSource: entityItemsSource("ListSaleCircuit")
            }
        })}

        {bld.text(a => a.name, {
            validators: [required]
        })}

        {bld.singleSelector(a => a.type, {
            validators: [required],
            editor: {
                itemsSource: enumItemsSource(SalePointType)
            }
        })}

        {bld.singleSelector(a => a.createMerchant, {
            validators: [required],
            label: "",
            style: ["no-box", "no-label"],
            onChanged: (m, v) => {
                m.merchantId = null;
            },
            editor: {
                template: SingleSelectorTemplates.Options,
                itemsSource: staticItemsSource(["msg-create-merchant", true], ["msg-existing-merchant", false])
            }
        })}

        {bld.singleSelector(a => a.merchantId, {
            validators: [required],
            visible: a => !a.createMerchant,
            label: "merchant",
            editor: {
                itemsSource: merchantItemsSource as any
            }
        })}

        {bld.content(forModel(m => <label visible={m.createMerchant}>{formatText("merchant-data")}</label>))}

        {bld.text(a => a.merchantName, {
            label: "name",
            validators: [required],
            visible: a => a.createMerchant,
        })}

        {bld.text(a => a.merchantEmail, {
            label: "email",
            validators: [required, validEmail],
            visible: a => a.createMerchant,

        })}
    </>, {
        commitMode: "manual",
        style: "vertical",
        inputField: { style: "filled" },
    })
    .asContent();


export const EditSalePointPage = ItemEditContent.bulder<IUpdate, IEditSalePointArgs>()

    .info({
        name: "sale-point-edit",
        route: "/sale-point/{id}/edit",
    })

    .options(() => ({
        title: formatText("edit-item", "sale-point"),
        style: ["panel"],
    }))

    .feature(Authorize<IEditSalePointArgs>(bld =>
        bld.businessAccount()
           .resource("SalePoint", "edit", bld.args.id)))

    .save(async value => {

        if (!value.hasAddress) {
            value.address = null;
        } 

        delete value.hasAddress;

        await executeAsync("UpdateSalePoint", {
           ...value
        })

        invalidateCache("ListSalePoint");

        return true;
    })

    .load(async args => {

        if (!args.value && args.listItem) {
            args.value = {
                id: args.listItem.id,
                saleCircuitId: undefined,
                name: args.listItem.name,
                type: args.listItem.type,
                address: args.listItem.address,
                hasAddress: notEmpty(args.listItem.address?.city),
                activeCatalogId: args.listItem.activeCatalog?.id,
                merchantId: args.listItem.merchant?.id,
                bankPosServiceId: args.listItem.activeBankPos?.id
            }
        }
            
        return true;
    })

    .editor(bld => <>

        {bld.text(a => a.name, {
            validators: [required]
        })}

        {bld.singleSelector(a => a.type, {
            validators: [required],
            editor: {
                itemsSource: enumItemsSource(SalePointType)
            }
        })}

        {bld.singleSelector(a => a.activeCatalogId, {
            editor: {
                emptyItem: formatText("[none-male]") as string, 
                itemsSource: entityItemsSource("ListCatalog"),
            }
        })}

        {bld.singleSelector(a => a.bankPosServiceId, {
            visible: a => (a.type & SalePointType.Charge) != 0,
            label: "bank-pos",
            editor: {
                emptyItem: formatText("[none-male]") as string,
                itemsSource: entityItemsSource("ListMerchantServices", false, {
                    merchantId: bld.container.value.merchantId,
                    type: MerchantServiceType.PhysicalBankPos
                }),
            }
        })}

        {bld.boolean(a => a.hasAddress)}

        {bld.editor(a => a.address, AddressEditor, {
            visible: a => a.hasAddress
        })}

    </>, {
        commitMode: "manual",
        style: "vertical",
        inputField: { style: "filled" },
    })
    .asContent();
